/**
 * Procurement API client (S-126-04 worksheet-driven path).
 *
 * Backend endpoints (Sprint 23 W0 S-126-02):
 *   GET   /api/v1/procurement-cart
 *   GET   /api/v1/procurement-requests
 *   POST  /api/v1/procurement-requests
 *   PATCH /api/v1/procurement-requests/:id
 */

import { api } from './client';

export type ProcurementCartStatus = 'ACTIVE' | 'SUBMITTED' | 'ORDERED' | 'CLOSED';
export type ProcurementRequestStatus = 'DRAFT' | 'SUBMITTED' | 'ORDERED' | 'RECEIVED' | 'CANCELLED';
export type ProcurementRequestSource = 'WORKSHEET' | 'SALES' | 'MANUAL' | 'AUTO_MIN';

export interface ProcurementCart {
  id: string;
  tenantId: string;
  status: ProcurementCartStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ProcurementRequest {
  id: string;
  tenantId: string;
  cartId: string;
  productId: string;
  quantity: string;
  source: ProcurementRequestSource;
  status: ProcurementRequestStatus;
  worksheetId: string | null;
  dayBucket: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcurementRequestFilter {
  status?: ProcurementRequestStatus;
  source?: ProcurementRequestSource;
  cartId?: string;
  worksheetId?: string;
  productId?: string;
}

export interface CreateProcurementRequestInput {
  productId: string;
  quantity: number | string;
  source: ProcurementRequestSource;
  worksheetId?: string;
  dayBucket?: string;
  notes?: string;
}

export interface UpdateProcurementRequestInput {
  quantity?: number | string;
  status?: ProcurementRequestStatus;
  notes?: string | null;
}

export async function getProcurementCart() {
  return api.get<{ data: ProcurementCart }>('/procurement-cart');
}

/**
 * A lapozás metaadata (#1034). **Opcionális**: amíg a BE-oldal nincs élesítve, a
 * végpont `meta` nélkül válaszol — ilyenkor a FE a régi, csonkítatlan viselkedést
 * feltételezi. Ha megjelenik, a `hasMore` az EGYETLEN jelzés arról, hogy a lista
 * nem teljes; enélkül a csonkítás NÉMA lenne.
 */
export interface ProcurementListMeta {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export async function listProcurementRequests(filter: ProcurementRequestFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.source) params.set('source', filter.source);
  if (filter.cartId) params.set('cartId', filter.cartId);
  if (filter.worksheetId) params.set('worksheetId', filter.worksheetId);
  if (filter.productId) params.set('productId', filter.productId);
  const qs = params.toString();
  const path = qs ? `/procurement-requests?${qs}` : '/procurement-requests';
  return api.get<{ data: ProcurementRequest[]; meta?: ProcurementListMeta }>(path);
}

export async function createProcurementRequest(input: CreateProcurementRequestInput) {
  return api.post<{ data: ProcurementRequest }>('/procurement-requests', input);
}

/**
 * Sales-driven convenience wrapper (S-126-05 slice 1, Sprint 24 W0 D-1).
 *
 * POS "nincs raktáron" → cart flow. Forces `source: 'SALES'` and omits the
 * worksheet/day-bucket fields (irrelevant for sales-driven path Phase 1;
 * sales-quote binding is Phase 2 placeholder per PRD seed §3 F4.2).
 *
 * Phase 2 will extend `SalesProcurementInput` with `salesQuoteId` once the
 * quote → cart sync ships.
 */
export interface SalesProcurementInput {
  productId: string;
  quantity: number | string;
  notes?: string;
}

export async function createSalesProcurementRequest(input: SalesProcurementInput) {
  return createProcurementRequest({
    productId: input.productId,
    quantity: input.quantity,
    source: 'SALES',
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

export async function updateProcurementRequest(id: string, input: UpdateProcurementRequestInput) {
  return api.patch<{ data: ProcurementRequest }>(`/procurement-requests/${id}`, input);
}

/**
 * S-126-08 sub-slice 8a/8b — Bulk cart-submit Phase 1.
 *
 * Moves every DRAFT request in the current tenant cart → SUBMITTED, and
 * flips the cart status ACTIVE → SUBMITTED. Idempotent: re-running on a
 * cart with zero DRAFTs still flips status (audit-trail clarity).
 *
 * 422 PROCUREMENT_CART_NOT_ACTIVE when cart is already past ACTIVE.
 */
export interface SubmitCartResult {
  cart: ProcurementCart;
  submittedCount: number;
  skippedCount: number;
  submittedRequestIds: string[];
}

export async function submitProcurementCart() {
  return api.post<{ data: SubmitCartResult }>('/procurement-cart/submit', {});
}

/**
 * S-126-08 sub-slice 8c — Bulk mark-group-as-ordered with optional
 * supplier-routing free-text notes.
 *
 * Moves every SUBMITTED request of the given source → ORDERED and appends
 * the supplier note to each request's notes field. If no open requests
 * remain in the cart, the cart status flips → ORDERED.
 */
export interface MarkGroupOrderedInput {
  source: ProcurementRequestSource;
  supplierNotes?: string;
}

export interface MarkGroupOrderedResult {
  cart: ProcurementCart;
  orderedCount: number;
  cartStatusChanged: boolean;
}

export async function markProcurementGroupOrdered(input: MarkGroupOrderedInput) {
  return api.post<{ data: MarkGroupOrderedResult }>('/procurement-cart/mark-group-ordered', input);
}
